import { RiCheckFill, RiSubtractLine } from '@remixicon/react';
import type { ComponentProps, ReactNode } from 'react';

import { type Size, cn } from '@playbook/utils';

import css from './styles.module.css';

export type CheckboxSize = Extract<Size, 'sm' | 'md'>;

const DefaultCheckboxSize: CheckboxSize = 'md' as const;

/**
 *	The box's outline. `square` is the checkbox as Figma draws it everywhere
 *	but one place: a poll's option, where the same `.checkbox` is drawn as a
 *	circle. That is a radius override on the instance rather than a variant in
 *	Figma, so it is a prop here rather than a mapped axis.
 */
export type CheckboxShape = 'square' | 'round';

const DefaultCheckboxShape: CheckboxShape = 'square' as const;

type CheckboxEventHandlers = {
	[K in keyof ComponentProps<'input'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'input'>[K];
};

interface CheckboxDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface CheckboxProperties
	extends CheckboxEventHandlers, CheckboxDataAttributes {
	size?: CheckboxSize;
	shape?: CheckboxShape;
	invalid?: boolean;
	disabled?: boolean;
	required?: boolean;

	/**
	 *	Neither checked nor unchecked — the box a partly-ticked group shows.
	 *	Figma calls this `value=mix`. It is a property of the element rather
	 *	than an attribute, so it is written in a ref callback.
	 */
	indeterminate?: boolean;

	'aria-describedby'?: string;

	/** The name of a bare checkbox, which has no visible `label` to give it one. */
	'aria-label'?: string;

	/**
	 *	A checkbox group is several boxes sharing one `name` and differing by
	 *	`value`, so the id has to carry the value too or every box in the group
	 *	answers to the same label.
	 */
	name: string;
	value?: string;
	id?: string;
	checked?: boolean;
	defaultChecked?: boolean;

	/** Text beside the box. Without it the checkbox is the bare control. */
	label?: string;
	description?: string;
}

/**
 *	Only the label is inside the `<label>`: a label's accessible name is its
 *	whole subtree, so the description sits outside it and is pointed at with
 *	`aria-describedby` instead.
 */

export function Checkbox({
	size = DefaultCheckboxSize,
	shape = DefaultCheckboxShape,
	invalid = false,
	disabled = false,
	required = false,
	indeterminate = false,

	'aria-describedby': describedBy,

	name,
	value,
	id,
	checked,
	defaultChecked,

	label,
	description,

	...props
}: CheckboxProperties): ReactNode {
	const fieldId: string =
		id ?? (value === undefined ? name : `${name}-${value}`);
	const className: string = cn(
		css.checkbox,
		css[size],
		css[shape],
		invalid && css.invalid
	);

	const control: ReactNode = (
		<span
			data-size={size}
			data-shape={shape}
			data-invalid={invalid}
			className={className}>
			<input
				{...props}
				ref={(element) => {
					if (element) {
						element.indeterminate = indeterminate;
					}
				}}
				type="checkbox"
				id={fieldId}
				name={name}
				value={value}
				checked={checked}
				defaultChecked={defaultChecked}
				disabled={disabled}
				required={required}
				aria-invalid={invalid || undefined}
				aria-describedby={description ? `${fieldId}-description` : describedBy}
				className={css.field}
			/>
			<span aria-hidden="true" className={css.box}>
				<RiCheckFill className={css.check} />
				<RiSubtractLine className={css.mix} />
			</span>
		</span>
	);

	if (!label) {
		return control;
	}

	return (
		<div data-size={size} className={cn(css.option, css[size])}>
			{control}
			<span className={css.text}>
				<label htmlFor={fieldId} className={css.label}>
					{label}
				</label>
				{description && (
					<span id={`${fieldId}-description`} className={css.description}>
						{description}
					</span>
				)}
			</span>
		</div>
	);
}
